import React, {useState} from 'react';
import Menu from 'react-burger-menu/lib/menus/slide';
import {FormattedMessage, useIntl} from 'react-intl';
import {Button} from '../../Button';
import {useLocalizedUrl} from '../../../hooks/useLocalizedUrl';
import {AccordionMenu} from './AccordionMenu';
import classNames from 'classnames';
import {MenuLink} from './MenuLink';
import '../../../css/burgerMenu.css';
import * as Logo from '../../icon/Logo';
import {config} from '../../../../config';
import {LOGIN_TEST_ID} from './data';
import {localeToPath} from '../../../utils/localeToPath';
import type {LocaleEnum} from '../../../types/locale';
import type {LinkPropType} from '../../Link';
import type {MenuSections} from './types';
import {useGetNavItems} from '../../../hooks/useGetNavItems';
import {SlimIcon} from '../../icon/SlimIcon';

type Props = {
  links: MenuSections;
  currentPathname: string;
};

export const BurgerMenu: React.FC<Props> = props => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const intl = useIntl();
  const localizeUrl = useLocalizedUrl();

  const linkClasses = 'w-full block p-3 font-body text-base font-semibold transition-all ease-in-out duration-300 cursor-pointer';

  const navigation = useGetNavItems(intl);

  const handleStateChange = ({isOpen}) => {
    setMenuIsOpen(isOpen);
  };

  const handleOnClose = () => {
    setMenuIsOpen(false);
  };

  return (
    <Menu
      className='bg-white max-w-md w-full'
      right
      // by default library adds an empty ID which makes W3 validator complain. By explicitly defying an ID fixes the issue, even though we don't need it.
      id='burger-menu'
      isOpen={menuIsOpen}
      bodyClassName='bm-overflow-y-hidden'
      onStateChange={handleStateChange}
    >
      <div className='absolute h-20 w-full bg-white z-10'>
        <div className='absolute left-10 top-5'>
          <MenuLink className='w-auto h-35px mb-3 text-primary' to={props.links.logoUrl} data-testid='navbar-logo-mobile'>
            <Logo.Small />
          </MenuLink>
        </div>
        <button onClick={handleOnClose} className='absolute p-0 border-none bg-transparent w-10 h-10 top-5 right-4 z-10 cursor-pointer'>
          <span className='absolute h-0.5 w-7 bg-black top-5 right-2 rotate-45'></span>
          <span className='absolute h-0.5 w-7 bg-black top-5 right-2 -rotate-45'></span>
        </button>
      </div>

      <div className='absolute w-full px-7 pb-4 mt-20 mb-36 overflow-scroll bottom-0 top-0'>
        {navigation.map((item, index) => (
          <div key={`${item.title}-${index}`} className='mb-4'>
            {item.submenu?.length > 0 && (
              <AccordionMenu
                key={`${item.title}-${index}`}
                linkClasses={linkClasses}
                title={item.title}
                links={item}
                bg={item.bg ? `${item.bg.hex}` : null}
                textColor={item.textColor ? `${item.textColor.hex}` : null}
                actionTextColor={item.actionTextColor ? `${item.actionTextColor.hex}` : null}
              />
            )}
            {!item.submenu?.length && (
              <ul className='m-0'>
                <li>
                  <MenuLink
                    className={classNames(linkClasses, 'px-3')}
                    active={Boolean(item.slug?.slug?.replace(config.siteUrl as string, '') === props.currentPathname)}
                    to={`/${localeToPath(intl.locale as LocaleEnum)}/${item.slug?.slug}/`}
                    bg={item.bg ? `${item.bg.hex}` : null}
                    // We need Transient props for styled components
                    // https://styled-components.com/docs/api#transient-props
                    $textColor={item.textColor ? `${item.textColor.hex}` : null}
                  >
                    <p className='m-0 text-md font-body font-normal leading-6'>{item.title}</p>
                  </MenuLink>
                </li>
              </ul>
            )}
          </div>
        ))}

        {props.links.other.map((entry, index) => {
          const targetProps = entry.targetBlank ? {target: '_blank' as LinkPropType['target'], rel: 'noopener noreferrer'} : {};

          if (entry.label === 'Login') {
            return null;
          }

          return (
            <ul className='m-0 mb-4' key={`${entry.href}-${index}`}>
              <li>
                <MenuLink
                  className={linkClasses}
                  data-testid={`${entry.dataTestId}-mobile`}
                  to={entry.href}
                  active={Boolean(entry.href.replace(config.siteUrl as string, '') === props.currentPathname)}
                  {...targetProps}
                >
                  <p className='m-0 text-md font-body font-normal leading-6'>{entry.label}</p>
                </MenuLink>
              </li>
            </ul>
          );
        })}
      </div>

      <div className='absolute bottom-0 sm:px-10 px-2 w-full bg-grey-bright h-36'>
        <ul>
          <li className='flex justify-center'>
            <Button noFloat={true} to={localizeUrl('/signup/start')} data-testid='signup-button-mobile'>
              <FormattedMessage id='header.testbutton' />
            </Button>
          </li>
        </ul>
        <ul className='px-3 sm:px-0 flex'>
          <li className='flex justify-center w-1/2'>
            <MenuLink to={`tel:${intl.formatMessage({id: 'header.phone.raw'})}`} data-testid='nav-phone-mobile'>
              <p className='text-base items-center sm:text-lg m-0 flex align-middle text-black hover:text-primary transition-all ease-in-out duration-300 cursor-pointer'>
                <SlimIcon className='pt-1' size='small' viewBoxSize='custom30x30' name='phone' />
                <span className='inline sm:hidden'>{intl.formatMessage({id: 'header.menu.call'})}</span>
                <span className='hidden sm:inline'>
                  <FormattedMessage id='header.phone.display' />{' '}
                </span>
              </p>
            </MenuLink>
          </li>
          <li className='flex justify-center w-1/2'>
            <MenuLink to={localizeUrl('/login')} data-testid={`${LOGIN_TEST_ID}-mobile`}>
              <p className='text-base sm:text-lg m-0 text-black hover:text-primary transition-all ease-in-out duration-300 cursor-pointer'>Login</p>
            </MenuLink>
          </li>
        </ul>
      </div>
    </Menu>
  );
};
